import type { Dayjs } from 'dayjs';

import {
  checklistReservedViews,
  newViewId,
} from 'pages/period-close/checklist-v2/configs/common';
import {
  reconReservedViews,
  reconNewViewId,
} from 'pages/period-close/reconciliation-v2/configs/common';

import { buildQueryParams } from 'utils/helper';

import type { ListViewParams } from 'types/checklist-v2/ChecklistViewStates';
import type { CloseOverviewTabs } from 'types/close/CloseOverviewCommon';
import type { ReconListViewParams } from 'types/reconciliation-v2/ReconViewStates';
import type { CommentFilterMode } from 'types/task/TaskComments';

// === CLOSE OVERVIEW ===
export interface InsightsURLFilters {
  entity: string;
  period: Dayjs;
}
const overviewTabPaths = {
  insights: 'insights',
  // timeline: 'timeline', // TODO: Add back when implemented
  monthClose: 'month-close',
} as const satisfies Record<string, CloseOverviewTabs>;
export const closeOverviewURLs = {
  base: '/close/overview',
  _baseRelativePath: 'overview',
  // Absolute child paths
  insights: `/close/overview/${overviewTabPaths.insights}`,
  insightsWithFilters: (filters: Partial<InsightsURLFilters>) => {
    return `${closeOverviewURLs.insights}?${buildQueryParams({ ...filters, period: filters.period?.format('YYYY-MM') })}`;
  },
  // timeline: `/close/overview/timeline`, // TODO: Add back when implemented
  monthClose: `/close/overview/${overviewTabPaths.monthClose}`,
} as const;

export const closeOverviewTabsMap = {
  insights: {
    label: 'Insights',
    path: closeOverviewURLs.insights,
    relativePath: overviewTabPaths.insights,
  },
  // timeline: { // TODO: Add back when implemented
  //   label: 'Timeline',
  //   path: '/close/overview/timeline',
  //   relativePath: 'timeline',
  // },
  'month-close': {
    label: 'Month Close',
    path: closeOverviewURLs.monthClose,
    relativePath: overviewTabPaths.monthClose,
  },
} as const satisfies Record<
  CloseOverviewTabs,
  { label: string; path: string; relativePath: string }
>;

export type TaskParams = {
  taskId: string;
  commentId?: string;
  replyId?: string;
  commentFilters?: {
    type: CommentFilterMode;
  };
};

export type TaskParamsWithView = TaskParams & {
  view?: string;
};

const getView = (view?: string) => view || checklistReservedViews.list;
const getReconView = (view?: string) => view || reconReservedViews.list;

// === TASK INFO relative paths ===
export const taskRelativePaths = {
  taskBase: ({ taskId }: TaskParams) => `task/${taskId}`,
  taskInfo: ({ taskId }: TaskParams) => `task/${taskId}/info`,
  taskActivity: ({ taskId }: TaskParams) => `task/${taskId}/activity`,
  taskComments: ({ taskId }: TaskParams) => `task/${taskId}/comments`,
  taskWorkflow: ({ taskId }: TaskParams) => `task/${taskId}/workflow`,
  taskCommentsWithFilters: ({ taskId, commentFilters }: TaskParams) =>
    `task/${taskId}/comments?${buildQueryParams(commentFilters ?? {})}`,
  taskSingleComment: ({ taskId, commentId, replyId }: TaskParams) =>
    `task/${taskId}/comments/${commentId || ''}${commentId && replyId ? `/replies/${replyId}` : ''}`,
} as const;
type TaskInfoURLs = {
  [K in keyof typeof taskRelativePaths]: (params: TaskParamsWithView) => string;
};

// === CLOSE CHECKLIST ===
export type ChecklistURLFilters = Partial<ListViewParams> & {
  backTo?: string;
};
export const closeChecklistURLs = {
  // --- Views ---
  base: '/close/checklist',
  _baseRelativePath: 'checklist',
  list: `/close/checklist/${checklistReservedViews.list}`,
  listNew: `/close/checklist/${checklistReservedViews.list}/new`,
  board: `/close/checklist/${checklistReservedViews.board}`,
  timeline: `/close/checklist/${checklistReservedViews.timeline}`,
  calendar: `/close/checklist/${checklistReservedViews.calendar}`,
  newView: `/close/checklist/${newViewId}`,
  // TODO: Add other view types when they are implemented
  newViewWithFilters: (filters: ChecklistURLFilters) =>
    `${closeChecklistURLs.newView}?${buildQueryParams({ ...filters, tabType: checklistReservedViews.list })}`,
  customView: (view?: string) => `/close/checklist/${getView(view)}`,
  // --- Task info paths ---
  ...(Object.fromEntries(
    Object.entries(taskRelativePaths).map(([key, value]) => [
      key,
      ({ view, ...params }: TaskParamsWithView) =>
        `${closeChecklistURLs.customView(view)}/${value(params)}`,
    ])
  ) as TaskInfoURLs satisfies TaskInfoURLs),
} as const;

// === CLOSE RECONCILIATION ===
export type ReconciliationURLFilters = Partial<ReconListViewParams> & {
  backTo?: string;
};
export const closeReconciliationURLs = {
  // --- Views ---
  base: '/close/reconciliations',
  _baseRelativePath: 'reconciliations',
  list: `/close/reconciliations/${reconReservedViews.list}`,
  newView: `/close/reconciliations/${reconNewViewId}`,
  newViewWithFilters: (filters: ReconciliationURLFilters) =>
    `${closeReconciliationURLs.newView}?${buildQueryParams({ ...filters, tabType: reconReservedViews.list })}`,
  customView: (view?: string) => `/close/reconciliations/${getReconView(view)}`,
  // --- Task info paths ---
  ...(Object.fromEntries(
    Object.entries(taskRelativePaths).map(([key, value]) => [
      key,
      ({ view, ...params }: TaskParamsWithView) =>
        `${closeReconciliationURLs.customView(view)}/${value(params)}`,
    ])
  ) as TaskInfoURLs satisfies TaskInfoURLs),
} as const;

export const closeJournalEntriesURLs = {
  base: '/close/journal-entries',
  _baseRelativePath: 'journal-entries',
} as const;

export const closeURLs = {
  base: '/close',
} as const;
